import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { GetShipTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getShipTosDataView } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";

interface OwnProps {
    extendedStyles?: ChangeCustomerShipToSelectorStyles;
    setParameter: (parameter: GetShipTosApiParameter) => void;
    parameter: GetShipTosApiParameter;
    searchText: string;
    setSearchText: (searchText: string) => void;
    enableWarehousePickup: boolean;
    fulfillmentMethod: string;
    shipTo?: ShipToModel;
    billToId?: string;
    allowSelectBillTo?: boolean;
    onSelect: (shipTo: ShipToModel) => void;
    onCreateNewAddressClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    isLoading?: boolean;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState, props: OwnProps) => ({
    shipTosDataView: getShipTosDataView(state, props.parameter),
});

export interface ChangeCustomerShipToSelectorStyles {
    shipToSelectorDynamicDropdown?: DynamicDropdownPresentationProps;
}

export const changeCustomerShipToSelectorStyles: ChangeCustomerShipToSelectorStyles = {};

const DEBOUNCE_MS = 300;

const ChangeCustomerShipToSelector: FC<Props> = ({
    extendedStyles,
    shipTo,
    billToId,
    shipTosDataView,
    onSelect,
    parameter,
    setParameter,
    searchText,
    setSearchText,
    enableWarehousePickup,
    fulfillmentMethod,
    isLoading,
}) => {
    const [styles] = useState(() => mergeToNew(changeCustomerShipToSelectorStyles, extendedStyles));

    const [debouncedSearch, setDebouncedSearch] = useState(searchText);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchText);
        }, DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [searchText]);

    const [options, setOptions] = useState<OptionObject[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [allShipTos, setAllShipTos] = useState<ShipToModel[]>([]);
    const dropdownRef = useRef<any>(null);

    let dropdownLabel = translate("Select Ship To");
    let dropdownPlaceholder = translate("Search or Select Ship To");

    if (enableWarehousePickup && fulfillmentMethod === FulfillmentMethod.PickUp) {
        dropdownLabel = translate("Recipient Address");
        dropdownPlaceholder = translate("Search or Select Recipient Address");
    }

    useEffect(() => {
        if (!billToId) {
            return;
        }
        setSearchText("");
        setCurrentPage(1);
        setOptions([]);
        setAllShipTos([]);
        setParameter({
            ...parameter,
            billToId,
            filter: undefined,
            page: 1,
            pageSize: parameter.pageSize || 20,
        });
    }, [billToId]);

    useEffect(() => {
        setCurrentPage(1);
        setOptions([]);
        setAllShipTos([]);
        setParameter({
            ...parameter,
            filter: debouncedSearch || undefined,
            page: 1,
            pageSize: parameter.pageSize || 20,
        });
    }, [debouncedSearch]);

    useEffect(() => {
        if (shipTosDataView.value) {
            setAllShipTos(prev => {
                const merged = [...prev, ...(shipTosDataView.value || [])];
                return merged.filter(
                    (item, index, self) => index === self.findIndex(i => i.id === item.id)
                );
            });

            const newOptions: OptionObject[] = shipTosDataView.value.map(st => ({
                optionText: st.label,
                optionValue: st.id,
            }));

            setOptions(prevOptions => {
                const allOptions = [...prevOptions, ...newOptions];

                let uniqueOptions = allOptions.filter(
                    (option, index, self) =>
                        index === self.findIndex(o => o.optionValue === option.optionValue)
                );

                if (shipTo) {
                    const matchesSearch = !debouncedSearch || shipTo.label.toLowerCase().includes(debouncedSearch.toLowerCase());

                    uniqueOptions = uniqueOptions.filter(opt => opt.optionValue !== shipTo.id);
                    if (matchesSearch) {
                        uniqueOptions.unshift({
                            optionText: shipTo.label,
                            optionValue: shipTo.id,
                        });
                    }
                }

                if (!shipTo && shipTosDataView.value && shipTosDataView.value.length === 1) {
                    onSelect(shipTosDataView.value[0]);
                }

                return uniqueOptions;
            });
        }
    }, [shipTosDataView, shipTo, onSelect]);

    const loadMoreShipTos = useCallback(() => {
        if (!shipTosDataView.value || !shipTosDataView.pagination) {
            return;
        }
        const pageSize = parameter.pageSize || 20;
        const { page, totalItemCount } = shipTosDataView.pagination;

        if (page * pageSize >= totalItemCount) {
            return;
        }

        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        setParameter({
            ...parameter,
            page: nextPage,
            pageSize,
            filter: debouncedSearch || undefined,
        });
    }, [shipTosDataView, currentPage, parameter, setParameter, debouncedSearch]);

    const handleScroll = useCallback((event: Event) => {
        const target = event.target as HTMLDivElement;
        if (!target) {return;}
        if (target.scrollTop + target.clientHeight >= target.scrollHeight - 20) {
            loadMoreShipTos();
        }
    }, [loadMoreShipTos]);

    const handleOpen = useCallback(() => {
        if (shipTo) {
            setAllShipTos([]);
            setOptions([]);
            setCurrentPage(1);

            setParameter({
                ...parameter,
                billToId,
                filter: debouncedSearch || undefined, 
                page: 1,
                pageSize: parameter.pageSize || 20,
            });
        }

        const dropdownElement = document.querySelector('[role="listbox"]') as HTMLDivElement;
        if (dropdownElement) {
            dropdownElement.addEventListener("scroll", handleScroll);
        }

        setTimeout(() => {
            if (dropdownRef.current) {
                dropdownRef.current.setState({ focusedOption: undefined });
            }
        }, 0);
    }, [shipTo, debouncedSearch, handleScroll, parameter, setParameter, billToId]);

    const handleClose = useCallback(() => {
        const dropdownElement = document.querySelector('[role="listbox"]') as HTMLDivElement;
        if (dropdownElement) {
            dropdownElement.removeEventListener("scroll", handleScroll);
        }
    }, [handleScroll]);

    useEffect(() => {
        const interval = setInterval(() => {
            const dropdownElement = document.querySelector('[role="listbox"]') as HTMLDivElement;
            if (dropdownElement) {
                dropdownElement.addEventListener("scroll", handleScroll);
                clearInterval(interval);
            }
        }, 100);

        return () => {
            const dropdownElement = document.querySelector('[role="listbox"]') as HTMLDivElement;
            dropdownElement?.removeEventListener("scroll", handleScroll);
        };
    }, [shipTosDataView, handleScroll]);

    const searchTextChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newSearchText = event.currentTarget.value;
        setSearchText(newSearchText);
        setCurrentPage(1);
        setOptions([]);
        setAllShipTos([]);
    };

    const selectCustomerHandler = (shipToId?: string) => {
        if (!shipToId) {return;}
        const found = allShipTos.find(st => st.id === shipToId);
        if (found) {
            onSelect(found);
        }
    };

    return (
        <DynamicDropdown
            {...styles.shipToSelectorDynamicDropdown}
            ref={dropdownRef}
            label={dropdownLabel}
            onSelectionChange={selectCustomerHandler}
            onInputChange={searchTextChanged}
            selected={options.find(option => option.optionValue === shipTo?.id)?.optionValue || ""}
            placeholder={dropdownPlaceholder}
            isLoading={isLoading}
            onOpen={handleOpen}
            onClose={handleClose}
            options={options}
            search={searchText} 
            data-test-selector="changeCustomerShipToSelector"
            filterOption={() => true}
        />
    );
};

export default connect(mapStateToProps)(ChangeCustomerShipToSelector);